import { Box, CircularProgress, Typography } from "@mui/material";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussViewer } from "modules/fluss/components/design-kit/FlussDesignKit";
import { FlussViewerState } from "modules/fluss/types/fluss";
import { orthogonalRuntimeDiagram } from "modules/fluss/utils/runtimeDiagram";

export const FlussRuntimeViewer = ({
    xml,
    state,
}: {
    xml: string;
    state: FlussViewerState;
}) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!containerRef.current || !xml) return;

        const viewer = new NavigatedViewer({ container: containerRef.current });
        let cancelled = false;

        viewer.importXML(orthogonalRuntimeDiagram(xml)).then(() => {
            if (cancelled) return;
            const canvas = viewer.get("canvas");
            const overlays = viewer.get("overlays");
            const markers = [
                { ids: state.traversed_element_ids, css: "fluss-path-traversed", overlay: "fluss-runtime-overlay--traversed" },
                { ids: state.active_element_ids, css: "fluss-path-active", overlay: "fluss-runtime-overlay--active" },
                { ids: state.incident_element_ids, css: "fluss-path-incident", overlay: "fluss-runtime-overlay--incident" },
            ];

            markers.forEach(({ ids, css, overlay }) => ids.forEach((elementId) => {
                try {
                    canvas.addMarker(elementId, css);
                    overlays.add(elementId, {
                        position: { top: -8, right: -8 },
                        html: `<span class="fluss-runtime-overlay ${overlay}" aria-hidden="true"></span>`,
                    });
                } catch {
                    // History can reference an element from an older malformed import; keep the viewer usable.
                }
            }));
            canvas.zoom("fit-viewport");
            setLoading(false);
        }).catch(() => {
            if (!cancelled) {
                setFailed(true);
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
            viewer.destroy();
        };
    }, [state, xml]);

    return (
        <FlussViewer ariaLabel={t("fluss.runtime.viewer_aria", { defaultValue: "Process progress" })}>
            <Box ref={containerRef} className="fluss-runtime-viewer" />
            {loading && (
                <Box className="fluss-runtime-viewer-state">
                    <CircularProgress size={28} />
                </Box>
            )}
            {failed && (
                <Box className="fluss-runtime-viewer-state">
                    <Typography color="error">
                        {t("fluss.runtime.viewer_error", { defaultValue: "The process diagram could not be loaded." })}
                    </Typography>
                </Box>
            )}
        </FlussViewer>
    );
};
